import { motion } from "framer-motion";
import { AlertTriangle, SearchX } from "lucide-react";
import { fadeUp, revealOnce } from "../lib/motion";

// Skeleton-first loading: content-shaped placeholders, no fake data.
export function PageLoading({ label = "Loading…" }) {
  return (
    <div className="py-16 space-y-4" data-testid="loading-state" role="status" aria-live="polite" aria-label={label}>
      <div className="skeleton h-7 w-48" />
      <div className="skeleton h-4 w-72" />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 pt-4">
        {[0, 1, 2, 3].map((i) => <div key={i} className="skeleton h-28" />)}
      </div>
      <span className="sr-only">{label}</span>
    </div>
  );
}

export function CardSkeleton({ count = 4 }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" aria-hidden="true">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="space-y-3">
          <div className="skeleton h-44" />
          <div className="skeleton h-4 w-3/4" />
          <div className="skeleton h-3 w-1/2" />
        </div>
      ))}
    </div>
  );
}

export function EmptyState({ title = "Nothing here yet", body, action, icon }) {
  return (
    <motion.div variants={fadeUp} {...revealOnce}
                className="flex flex-col items-center justify-center py-24 px-6 text-center border border-dashed border-[#27272A] rounded-2xl"
                data-testid="empty-state">
      <div className="w-12 h-12 rounded-xl bg-[#E50914]/10 flex items-center justify-center mb-6">
        {icon || <SearchX className="w-6 h-6 text-[#FF3B45]" />}
      </div>
      <h3 className="type-h3">{title}</h3>
      {body && <p className="mt-2.5 text-sm text-zinc-500 max-w-sm leading-relaxed">{body}</p>}
      {action && <div className="mt-7">{action}</div>}
    </motion.div>
  );
}

export function ErrorState({ title = "Something went wrong", body, onRetry }) {
  return (
    <motion.div variants={fadeUp} {...revealOnce}
                className="flex flex-col items-center justify-center py-24 px-6 text-center border border-dashed border-[#27272A] rounded-2xl"
                data-testid="error-state" role="alert">
      <div className="w-12 h-12 rounded-xl bg-red-500/10 flex items-center justify-center mb-6">
        <AlertTriangle className="w-6 h-6 text-red-400" />
      </div>
      <h3 className="type-h3">{title}</h3>
      <p className="mt-2.5 text-sm text-zinc-500 max-w-sm leading-relaxed">{body || "We couldn't load this right now. Please try again."}</p>
      {onRetry && <button data-testid="error-retry-btn" onClick={onRetry} className="btn-secondary mt-7 text-sm">Try Again</button>}
    </motion.div>
  );
}
